import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { deleteAccount, AccountDeletionError } from "../../api/account/deleteAccount";
import { useUserControls } from "../../context/UserControlsProvider";

type DeleteAccountDialogProps = {
  open: boolean;
  onClose: () => void;
  onDeleted?: () => Promise<void> | void;
};

const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({
  open,
  onClose,
  onDeleted,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showSnackbar } = useUserControls();

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    setError(null);

    try {
      await deleteAccount();
      showSnackbar?.({
        message: "Your account has been permanently deleted.",
        severity: "success",
        duration: 6000,
      });
      onClose();
      await onDeleted?.();
    } catch (err) {
      console.error("[DeleteAccountDialog] Failed to delete account", err);
      const message =
        err instanceof AccountDeletionError
          ? err.message
          : "Unable to delete account. Please try again.";
      setError(message);
      showSnackbar?.({
        message,
        severity: "error",
        duration: 6000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isDeleting) {
          setError(null);
          onClose();
        }
      }}
      aria-labelledby="delete-account-dialog-title"
    >
      <DialogTitle
        id="delete-account-dialog-title"
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <WarningAmberRoundedIcon color="error" />
        Delete Account
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="body1">
          Deleting your account will remove your profile, saved preferences, and
          any associated data from Cabinet Logger. This action cannot be undone.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          If you simply want to pause your access, consider signing out instead.
        </Typography>
        {error ? (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: 1,
              backgroundColor: (theme) => theme.palette.error.light,
              color: (theme) => theme.palette.error.contrastText,
            }}
          >
            <Typography variant="body2">{error}</Typography>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={() => {
            setError(null);
            onClose();
          }}
          disabled={isDeleting}
        >
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={handleConfirmDelete}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} color="inherit" />
              Deleting...
            </Box>
          ) : (
            "Delete Account"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteAccountDialog;
